import { Cpu, ShieldCheck, Activity, Award, ArrowRight } from "lucide-react";
import { ENPHASE_IMG } from "../lib/constants";
import { useScrollTo } from "../hooks/useScrollTo";

const POINTS = [
  { icon: Activity, title: "Higher Efficiency", desc: "Module-level MPPT extracts maximum yield from every panel — even with partial shading." },
  { icon: ShieldCheck, title: "Enhanced Safety", desc: "Low-voltage AC architecture eliminates high-voltage DC risks on your rooftop." },
  { icon: Cpu, title: "Module-Level Monitoring", desc: "Track each panel's performance in real time from your phone." },
  { icon: Award, title: "Long-Term Performance", desc: "Industry-leading warranties and proven reliability for decades of clean energy." },
];

export const Enphase = () => {
  const scrollTo = useScrollTo();
  return (
    <section
      id="enphase"
      data-testid="enphase-section"
      className="relative py-20 lg:py-28 bg-white overflow-hidden"
    >
      {/* Decorative */}
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#F6B800]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#2E8B57]/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="chapter-label tt-reveal" data-testid="enphase-chapter-label"><span>05 / INTELLIGENCE IN EVERY PANEL</span><span>ENPHASE MICROINVERTER TECHNOLOGY</span></div>
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 tt-reveal order-2 lg:order-1">
            <h2 className="eyebrow mb-5" data-testid="enphase-heading">ENPHASE TECHNOLOGY</h2>
            <p className="section-statement mb-6" data-testid="enphase-statement">Small technology.<br /><span>Brighter possibilities.</span></p>
            <p className="enphase-description mb-8">
              Tech Thrissur offers advanced Enphase Microinverter technology — the
              gold standard in modern solar. Each panel runs independently, giving
              you better performance, safety, and visibility than traditional
              string inverters.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {POINTS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`enphase-point-${i}`}
                    className="flex gap-3 items-start p-4 rounded-2xl bg-[#F8FAFC] border border-slate-100 hover:border-[#F6B800]/40 hover:bg-white transition-all duration-300"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#0F4C81] text-white flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-display font-semibold text-slate-900 text-[15px] mb-1">{p.title}</h4>
                      <p className="text-slate-600 text-[13.5px] leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => scrollTo("contact")}
              data-testid="enphase-cta"
              className="inline-flex items-center gap-2 bg-[#0F4C81] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#0c3d68] hover:scale-[1.04] transition-all duration-300 shadow-lg"
            >
              Talk to an Expert
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="lg:col-span-6 tt-reveal order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#F6B800]/25 to-[#2E8B57]/15 rounded-[2rem] blur-2xl" />
              <div className="enphase-banner relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(15,76,129,0.18)] border border-white">
                <img
                  data-testid="enphase-image"
                  loading="lazy"
                  src={ENPHASE_IMG}
                  alt="TECH THRISSUR — Enphase Gold Installer"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
